import { shallowComparison, deepComparison } from "./compareUtils";

type ResultObjectComparerBuilder<T extends Record<string, unknown>> = {
	getResult: () => ((a: T, b: T) => boolean);
};

type ConfigurationObjectComparerBuilder<T extends Record<string, unknown>, TKeysToCompare extends StringKeyOf<T>> = {
	equals: <TPropertyKeys extends TKeysToCompare>(
		keys: (readonly TPropertyKeys[]) | TPropertyKeys
	) => IObjectComparerBuilder<T, Exclude<TKeysToCompare, TPropertyKeys>>;
	shallow: <TPropertyKeys extends TKeysToCompare>(
		keys: (readonly TPropertyKeys[]) | TPropertyKeys
	) => IObjectComparerBuilder<T, Exclude<TKeysToCompare, TPropertyKeys>>;
	deep: <TPropertyKeys extends TKeysToCompare>(
		keys: (readonly TPropertyKeys[]) | TPropertyKeys
	) => IObjectComparerBuilder<T, Exclude<TKeysToCompare, TPropertyKeys>>;
};

export type IObjectComparerBuilder<T extends Record<string, unknown>, TKeysToCompare extends StringKeyOf<T>> = [TKeysToCompare] extends [never]
	? ResultObjectComparerBuilder<T>
	: ConfigurationObjectComparerBuilder<T, TKeysToCompare>;

class ObjectComparerBuilder<T extends Record<string, unknown>> implements
	ResultObjectComparerBuilder<T>,
	ConfigurationObjectComparerBuilder<T, StringKeyOf<T>>
{
	private readonly _propertiesToExactCompare: StringKeyOf<T>[];
	private readonly _propertiesToShallowCompare: StringKeyOf<T>[];
	private readonly _propertiesToDeepCompare: StringKeyOf<T>[];

	constructor() {
		this._propertiesToExactCompare = [];
		this._propertiesToShallowCompare = [];
		this._propertiesToDeepCompare = [];
	}

	public equals<TPropertyKeys extends StringKeyOf<T>>(
		keys: TPropertyKeys | readonly TPropertyKeys[]
	): IObjectComparerBuilder<T, Exclude<StringKeyOf<T>, TPropertyKeys>> {
		this.addToCollection(this._propertiesToExactCompare, keys);
		return this;
	}

	public shallow<TPropertyKeys extends StringKeyOf<T>>(
		keys: TPropertyKeys | readonly TPropertyKeys[]
	): IObjectComparerBuilder<T, Exclude<StringKeyOf<T>, TPropertyKeys>> {
		this.addToCollection(this._propertiesToShallowCompare, keys);
		return this;
	}

	public deep<TPropertyKeys extends StringKeyOf<T>>(
		keys: TPropertyKeys | readonly TPropertyKeys[]
	): IObjectComparerBuilder<T, Exclude<StringKeyOf<T>, TPropertyKeys>> {
		this.addToCollection(this._propertiesToDeepCompare, keys);
		return this;
	}

	public getResult(): ((a: T, b: T) => boolean) {
		return (a: T, b: T): boolean => {
			for (const property of this._propertiesToExactCompare) {
				if (!Object.is(a[property], b[property])) {
					return false;
				}
			}

			for (const property of this._propertiesToShallowCompare) {
				if (!shallowComparison(a[property], b[property])) {
					return false;
				}
			}

			for (const property of this._propertiesToDeepCompare) {
				if (!deepComparison(a[property], b[property])) {
					return false;
				}
			}

			return true;
		};
	}

	private addToCollection(
		collection: StringKeyOf<T>[],
		keys: StringKeyOf<T> | readonly StringKeyOf<T>[]
	): void {
		if (typeof keys === "string") {
			collection.push(keys);
		} else {
			collection.push(...keys);
		}
	}
}

export function createObjectComparer<T extends Record<string, unknown>>(): IObjectComparerBuilder<T, StringKeyOf<T>>{
	return new ObjectComparerBuilder<T>();
}
